import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import PasswordInput from "../components/PasswordInput";
import { ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try { await login(email, password); nav("/"); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-8 fade-up">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-teal-700 text-white flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
            <div>
              <div className="font-display text-xl font-bold text-slate-900">{t("app.name")}</div>
              <div className="text-xs text-slate-500">{t("app.tagline")}</div>
            </div>
          </div>
          <LanguageSwitcher compact />
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">{t("auth.welcome_back")}</h1>
        <p className="text-sm text-slate-500 mb-6">{t("auth.welcome_sub")}</p>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-sm text-slate-700 font-medium">{t("auth.email")}</label>
            <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 w-full border border-slate-300 rounded-xl px-3 py-2.5 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" placeholder="you@example.com" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm text-slate-700 font-medium">{t("auth.password")}</label>
              <Link data-testid="login-forgot-link" to="/forgot-password" className="text-xs text-teal-700 font-medium hover:underline">Forgot password?</Link>
            </div>
            <PasswordInput testId="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
          </div>
          {error && <div className="text-sm bg-red-50 text-red-700 border border-red-200 px-3 py-2 rounded-xl" data-testid="login-error">{error}</div>}
          <button data-testid="login-submit" disabled={loading} className="w-full bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white rounded-xl py-3 font-semibold transition-colors">
            {loading ? t("auth.signing_in") : t("auth.sign_in")}
          </button>
        </form>

        <p className="text-sm text-slate-500 mt-6 text-center">
          {t("auth.no_account")} <Link data-testid="login-to-register" to="/register" className="text-teal-700 font-medium hover:underline">{t("auth.create")}</Link>
        </p>
        <p className="text-xs text-slate-400 mt-6 text-center">{t("auth.emergency_note")}</p>
      </div>
    </div>
  );
}
